import { useState, useCallback, memo, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faApple } from "@fortawesome/free-brands-svg-icons";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { useWidgetSettings } from "../WidgetSettings";

// Helper function to extract Apple Music ID from various URL formats
const extractAppleMusicId = (url) => {
  if (!url) return null;

  // Handle Apple Music URL format (music.apple.com/country/type/name/id)
  const urlMatch = url.match(
    /music\.apple\.com\/[a-z]{2}\/(song|album|playlist|artist)\/[^/]+\/(\d+)/
  );
  if (urlMatch) {
    return {
      type: urlMatch[1],
      id: urlMatch[2],
    };
  }

  // Handle direct ID input - try to determine type based on length
  const directIdMatch = url.match(/^(\d+)$/);
  if (directIdMatch) {
    const id = directIdMatch[1];
    // Default to song for direct IDs
    return { type: "song", id };
  }

  return null;
};

const AppleMusicConfigForm = memo(function AppleMusicConfigForm({
  config,
  onUpdate,
}) {
  const { setActionButton, clearActionButton } = useWidgetSettings();

  const [formData, setFormData] = useState({
    appleMusicUrl: config.appleMusicUrl || "",
    showLiveListening: config.showLiveListening || false,
    isAuthenticated: config.isAuthenticated || false,
    userProfile: config.userProfile || null,
    fullBackgroundArt: config.fullBackgroundArt || false,
  });

  const [urlError, setUrlError] = useState("");
  const [urlValidated, setUrlValidated] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [authError, setAuthError] = useState("");

  // Add handler to stop propagation
  const stopPropagation = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.nativeEvent?.stopImmediatePropagation();
    }
  }, []);

  const validateAppleMusicUrl = useCallback((url) => {
    if (!url.trim()) {
      setUrlError("");
      setUrlValidated(false);
      return false;
    }

    const appleMusicData = extractAppleMusicId(url);
    if (!appleMusicData) {
      setUrlError(
        "Please enter a valid Apple Music URL (song, album, playlist, or artist)"
      );
      setUrlValidated(false);
      return false;
    }

    setUrlError("");
    setUrlValidated(true);
    return true;
  }, []);

  const handleUrlChange = useCallback(
    (value) => {
      setFormData((prev) => ({ ...prev, appleMusicUrl: value }));
      validateAppleMusicUrl(value);
    },
    [validateAppleMusicUrl]
  );

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Validate URL if provided
      if (
        formData.appleMusicUrl &&
        !validateAppleMusicUrl(formData.appleMusicUrl)
      ) {
        return;
      }

      // Update the widget config
      onUpdate(formData);
    },
    [formData, onUpdate, validateAppleMusicUrl]
  );

  const getPreviewInfo = () => {
    if (!formData.appleMusicUrl) return null;

    const appleMusicData = extractAppleMusicId(formData.appleMusicUrl);
    if (!appleMusicData) return null;

    const typeLabels = {
      song: "Song",
      playlist: "Playlist",
      album: "Album",
      artist: "Artist",
    };

    return {
      type: typeLabels[appleMusicData.type] || "Unknown",
      id: appleMusicData.id,
    };
  };

  const previewInfo = getPreviewInfo();

  // Set up action button
  useEffect(() => {
    const actionButton = (
      <button
        type="button"
        onClick={handleSubmit}
        className="w-full px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
      >
        <FontAwesomeIcon icon={faApple} className="w-4 h-4" />
        Save Apple Music Widget
      </button>
    );

    setActionButton(actionButton);

    return () => {
      clearActionButton();
    };
  }, [setActionButton, clearActionButton, handleSubmit]);

  return (
    <div
      className="space-y-6"
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 pb-2 border-b border-white/10">
          <FontAwesomeIcon icon={faApple} className="w-6 h-6 text-gray-500" />
          <h3 className="text-lg font-semibold text-white">
            Apple Music Configuration
          </h3>
        </div>

        {/* Apple Music URL Input */}
        <div className="space-y-3">
          <Label className="text-white font-medium">Apple Music URL</Label>
          <Input
            value={formData.appleMusicUrl}
            onChange={(e) => {
              stopPropagation(e);
              handleUrlChange(e.target.value);
            }}
            placeholder="https://music.apple.com/us/album/..."
            className="bg-white/5 border-white/10 text-white placeholder:text-white/50 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/20 break-all whitespace-normal"
            style={{
              wordBreak: "break-all",
              whiteSpace: "normal",
              lineHeight: "1.4",
              minHeight: "2.5rem",
              height: "auto",
              padding: "0.75rem",
            }}
          />

          {urlError && (
            <p className="text-sm text-red-400 flex items-center gap-2">
              <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4" />
              {urlError}
            </p>
          )}

          {urlValidated && previewInfo && (
            <div className="text-sm text-green-400 flex items-center gap-2">
              <FontAwesomeIcon icon={faApple} className="w-4 h-4" />
              <span>Valid {previewInfo.type} detected</span>
            </div>
          )}

          {/* Developer Token Info */}
        </div>

        {/* Full Background Art Toggle */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-white font-medium">
                Full Background Art
              </Label>
              <p className="text-xs text-white/60 mt-1">
                Use album/playlist art as full widget background
              </p>
            </div>
            <Switch
              checked={formData.fullBackgroundArt}
              onCheckedChange={(checked) => {
                const newFormData = { ...formData, fullBackgroundArt: checked };
                setFormData(newFormData);
                // Update widget immediately
                onUpdate(newFormData);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

AppleMusicConfigForm.displayName = "AppleMusicConfigForm";

export default AppleMusicConfigForm;
